import React from 'react';
import styled from 'styled-components';

const QualificationContainer = styled.section`
  id: qualification;
  padding: ${({ theme }) => `${theme.spacing.content.section} ${theme.spacing.content.base}`};
  background-image: url('/images/map-of-america-highlighting-target-regions.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;

  /* Light white overlay for text readability */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.88);
    z-index: 0;
  }

  /* All child elements need to be above the overlay */
  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing['3xl']} ${theme.spacing.content.narrow}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing['2xl']} ${theme.spacing.content.narrow}`};
  }
`;



const QualificationContent = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  text-align: center;
`;

const QualificationTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }
`;

const QualificationSubtitle = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing['3xl']} 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const AssessmentContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  }
`;

const CriteriaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: ${({ theme }) => theme.spacing.base};
  }
`;

const CriteriaCard = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadowsEnhanced.card};
  text-align: left;
  transition: all ${({ theme }) => theme.transitionsEnhanced.base};
  border-left: 4px solid ${({ theme, $category }) => 
    $category === 'revenue' ? theme.colors.status.success :
    $category === 'industry' ? theme.colors.primary.main :
    $category === 'authority' ? theme.colors.secondary.main :
    $category === 'geographic' ? theme.colors.primary.light :
    theme.colors.primary.main};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadowsEnhanced.cardHover};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;



const CriteriaTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }
`;

const CriteriaDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const CriteriaList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CriteriaItem = styled.li`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.muted};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  padding-left: ${({ theme }) => theme.spacing.base};
  position: relative;

  &:before {
    content: "•";
    position: absolute;
    left: 0;
    color: ${({ theme, $category }) => 
      $category === 'revenue' ? theme.colors.status.success :
      $category === 'industry' ? theme.colors.primary.main :
      $category === 'authority' ? theme.colors.secondary.main :
      $category === 'geographic' ? theme.colors.primary.light :
      theme.colors.primary.main};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;





const QualificationSection = () => {
  console.log('QualificationSection updated - 2x2 grid layout with image container');

  return (
    <QualificationContainer id="qualification">
      <QualificationContent>
        <QualificationTitle>Strategic Fit Assessment</QualificationTitle>
        <QualificationSubtitle>
          Ensuring productive consultation time through mutual evaluation of strategic alignment 
          and collaboration potential for long-term technology leadership partnership.
        </QualificationSubtitle>

        <AssessmentContainer>
          <CriteriaGrid>
            <CriteriaCard $category="revenue">

              <CriteriaTitle>Revenue Range</CriteriaTitle>
              <CriteriaDescription>
                Sufficient scale to justify strategic technology investment with growth trajectory 
                requiring enhanced operational efficiency.
              </CriteriaDescription>
              <CriteriaList>
                <CriteriaItem $category="revenue">Annual revenue between $100M-$2B</CriteriaItem>
                <CriteriaItem $category="revenue">Growth trajectory requiring operational enhancement</CriteriaItem>
                <CriteriaItem $category="revenue">Technology investment capacity</CriteriaItem>
              </CriteriaList>
            </CriteriaCard>

            <CriteriaCard $category="industry">

              <CriteriaTitle>Industry Focus</CriteriaTitle>
              <CriteriaDescription>
                Manufacturing sectors with complex operational environments requiring IT/OT integration 
                and regulatory compliance capabilities.
              </CriteriaDescription>
              <CriteriaList>
                <CriteriaItem $category="industry">Manufacturing, automotive, food/beverage</CriteriaItem>
                <CriteriaItem $category="industry">Chemical/pharmaceutical operations</CriteriaItem>
                <CriteriaItem $category="industry">Complex IT/OT integration challenges</CriteriaItem>
                <CriteriaItem $category="industry">Safety-critical operational requirements</CriteriaItem>
              </CriteriaList>
            </CriteriaCard>

            <CriteriaCard $category="authority">

              <CriteriaTitle>Authority and Urgency</CriteriaTitle>
              <CriteriaDescription>
                Decision-making capability for technology strategy implementation with commitment 
                to long-term competitive positioning.
              </CriteriaDescription>
              <CriteriaList>
                <CriteriaItem $category="authority">Technology strategy decision authority</CriteriaItem>
                <CriteriaItem $category="authority">Operational challenges requiring immediate response</CriteriaItem>
                <CriteriaItem $category="authority">Long-term partnership commitment</CriteriaItem>
                <CriteriaItem $category="authority">Strategic rather than tactical focus</CriteriaItem>
              </CriteriaList>
            </CriteriaCard>

            <CriteriaCard $category="geographic">

              <CriteriaTitle>Geographic Focus</CriteriaTitle>
              <CriteriaDescription>
                Primary concentration in key manufacturing regions with consideration for 
                qualified global enterprise opportunities.
              </CriteriaDescription>
              <CriteriaList>
                <CriteriaItem $category="geographic">Midwest Manufacturing Belt priority</CriteriaItem>
                <CriteriaItem $category="geographic">Southeast Industrial Corridor focus</CriteriaItem>
                <CriteriaItem $category="geographic">Texas manufacturing hub</CriteriaItem>
                <CriteriaItem $category="geographic">Global enterprises considered</CriteriaItem>
              </CriteriaList>
            </CriteriaCard>
          </CriteriaGrid>
        </AssessmentContainer>
      </QualificationContent>
    </QualificationContainer>
  );
};

export default QualificationSection;